// Phase 3 (PLAN_DRAWING_SSOT_MIGRATION_2026-09-27): the React half lives in
// engine-react/src/drawing-ui and can only reach this package through its root
// entry, so every pure symbol it consumes is re-exported here. Additive only.
// Phase 2 (PLAN_DRAWING_SSOT_MIGRATION_2026-09-27) split this barrel: the drawing
// UI lives in engine-react/src/drawing-ui and is re-exported from there. Everything
// below is framework-free on purpose - engine-drawing must never import React.
// Symbols moved out (re-created in engine-react during Phase 3):
//   FloatingSelectionToolbar, DrawingContextMenu, AlertNotificationToast,
//   useDrawingInteraction, useDrawingStorage, useDrawingWorkspaceController,
//   useDrawingAlertEvaluator, useLongPress, filterDrawingsByScope,
//   renderDrawingToSvg + their exported types.
import { createDraftFromTool, createTool, getDrawingPlugin, isDrawingToolName, listDrawingPlugins, listDrawingTools, registerDrawingPlugin, registerDrawingTool } from "./registry";
import { drawingReducer } from "./stateMachine";
import { createDrawingHistory, historyReducer } from "./history";
import { chartPointToPixel, pixelToChartPoint } from "./coordinateUtils";
import { buildRenderScalesFromBridge, chartPointToPixelWithBridge, measurementPointToPixelWithBridge, resolveChartPointWithBridge, resolveMeasurementPointWithBridge, } from "./coordinateBridge";
import { measurementPointToPixel, resolveMeasurementPoint, summarizeMeasurement } from "./measuring";
import { createLocalStorageAdapter, createRESTStorageAdapter, DrawingImportError, DrawingStorageError } from "./DrawingStorage";
import { createDrawingInteractionState, deleteSelectedInteractionState, drawingInteractionReducer } from "./drawingInteractionState";
import { deserializeDrawingHistory, deserializeDrawingObject, deserializeDrawings, serializeDrawingHistory, serializeDrawingObject, serializeDrawings, exportDrawingsJSON, importDrawingsJSON, } from "./serialization";
import TrendLine from "./builtin/trendLine";
import HLine from "./builtin/hLine";
import VLine from "./builtin/vLine";
import Fibonacci from "./builtin/fibonacci";
import Channel from "./builtin/channel";
import Text from "./builtin/text";
import Rectangle from "./builtin/rectangle";
import CircleShape from "./builtin/circleShape";
import EllipseShape from "./builtin/ellipseShape";
import TriangleShape from "./builtin/triangleShape";
import CalloutShape from "./builtin/calloutShape";
import PriceLabelShape from "./builtin/priceLabelShape";
import Arrow from "./builtin/arrow";
import Ray from "./builtin/ray";
import ExtendedLine from "./builtin/extendedLine";
import Polyline from "./builtin/polyline";
import DateAndPriceRange from "./builtin/dateAndPriceRange";
import LongPosition from "./builtin/longPosition";
import ShortPosition from "./builtin/shortPosition";
import FibExtension from "./builtin/fibExtension";
import ParallelChannel from "./builtin/parallelChannel";
import Pitchfork from "./builtin/pitchfork";
import AbcdPattern from "./builtin/abcdPattern";
import FibArc from "./builtin/fibArc";
import FibTimeZone from "./builtin/fibTimeZone";
import RegressionChannel from "./builtin/regressionChannel";
import GannFan from "./builtin/gannFan";
import { ElliottImpulse, ElliottCorrection, ElliottTriangle, ElliottDoubleCombo, ElliottTripleCombo } from "./builtin/elliottWaves";
import { XabcdPattern, CypherPattern, HeadAndShoulders, TrianglePattern, ThreeDrivesPattern } from "./builtin/harmonicPatterns";
import { FibChannel, FibSpeedResistanceFan, TrendBasedFibTime, FibCircles, FibSpiral, FibSpeedResistanceArcs, FibWedge, Pitchfan } from "./builtin/fibExtended";
import { GannBox, GannSquare, GannSquareFixed } from "./builtin/gannAdvanced";
import { CyclicLines, TimeCycles, SineLine } from "./builtin/cycles";
import DisjointChannel from "./builtin/disjointChannel";
import FlatTopBottom from "./builtin/flatTopBottom";
import SchiffPitchfork from "./builtin/schiffPitchfork";
import ModifiedSchiff from "./builtin/modifiedSchiff";
import InsidePitchfork from "./builtin/insidePitchfork";
import GannFixed from "./builtin/gannFixed";
import TrendAngle from "./builtin/trendAngle";
import InfoLine from "./builtin/infoLine";
// CE24: Drawing Tools Pack 3
import AnchoredVWAP from "./builtin/anchoredVwap";
import IconMarker from "./builtin/iconMarker";
import GhostFeed from "./builtin/ghostFeed";
import BarsPattern from "./builtin/barsPattern";
import CrossLine from "./builtin/crossLine";
import DatePriceRange from "./builtin/datePriceRange";
// CE23: Drawing Tools Pack 2
import Brush from "./builtin/brush";
import Highlighter from "./builtin/highlighter";
import Eraser from "./builtin/eraser";
import Curve from "./builtin/curve";
import Arc from "./builtin/arc";
import Path from "./builtin/path";
import Polygon from "./builtin/polygon";
import RotatedRectangle from "./builtin/rotatedRectangle";
export { DRAWING_UI_TOKENS, createDrawingUiStyleVars } from "./uiTokens";
export { translateDrawingsByIds, resizeDrawingsByIds } from "./layerUtils";
// REMOVED 2026-09-30 (see CHANGELOG.md 2.0.2):
//   DRAWING_SHORTCUTS, resolveDrawingShortcut,
//   DrawingShortcutAction | DrawingShortcutCommand | DrawingShortcutDefinition | DrawingShortcutTool
// Canonical shortcuts live in the widget at src/widget/config/toolShortcuts.ts (TOOL_SHORTCUT_KEYS
// / ALT_TOOL_SHORTCUTS) + src/widget/hooks/useWidgetDrawings.ts. This table was @deprecated since
// S2.1 and had 0 consumers; keeping it exported was a second-source-of-truth trap.
export { createDrawingSelectionToolbarModel, resolveDrawingSelectionToolbarPosition, } from "./workspace/selectionToolbar";
// DT-10: Drawing Alerts
export { evaluateDrawingAlerts, resolveReferencePriceForAlert, isCooldownElapsed, detectPriceCross } from "./evaluateDrawingAlerts";
export { renderDrawingAlertIcon } from "./renderCanvas";
// DT-11: Elliott Wave
export { calculateElliottWaveGeometry, ELLIOTT_LABELS, ELLIOTT_POINT_COUNT } from "./builtin/elliottWaves";
// DT-12: Harmonic Patterns
export { calculateHarmonicPatternGeometry, PATTERN_POINT_LABELS, PATTERN_POINT_COUNT, PATTERN_HAS_CLOSING_LINE } from "./builtin/harmonicPatterns";
// DT-13: Extended Fibonacci
export { calculateFibExtendedGeometry, FIB_EXTENDED_LEVELS, FIB_FAN_RATIOS, FIB_EXTENDED_TWO_POINT, FIB_EXTENDED_THREE_POINT } from "./builtin/fibExtended";
// DT-14: Gann Advanced
export { calculateGannAdvancedGeometry, GANN_FRACTIONS } from "./builtin/gannAdvanced";
// DT-15: Cycle Tools
export { calculateCycleGeometry } from "./builtin/cycles";
export { clearDrawingStyleOverride, clearDrawingStyleOverrides, getDrawingStyleOverride, listDrawingStyleOverrides, overrideDrawingStyle, resolveDrawingStyle, subscribeDrawingStyle, subscribeDrawingStyleChanges, saveDrawingTemplate, loadDrawingTemplate, clearDrawingTemplate, applyDrawingTemplate, validateDrawingStyleTemplate } from "./drawingStyleRegistry";
export { listTemplates, saveTemplate, deleteTemplate, loadTemplate, exportAllTemplates, importAllTemplates } from "./drawingTemplateStorage";
export { isLineFamilyType, LINE_FAMILY_TYPES, lineTypeFlagsFor } from "./stateMachine";
import { resolvePointAnchor } from "./shared";
registerDrawingTool(TrendLine);
registerDrawingTool(HLine);
registerDrawingTool(VLine);
registerDrawingTool(Fibonacci);
registerDrawingTool(Channel);
registerDrawingTool(Text);
registerDrawingTool(Rectangle);
registerDrawingTool(CircleShape);
registerDrawingTool(EllipseShape);
registerDrawingTool(TriangleShape);
registerDrawingTool(CalloutShape);
registerDrawingTool(PriceLabelShape);
import PriceRange from "./builtin/priceRange";
import DateRange from "./builtin/dateRange";
import BarsCount from "./builtin/barsCount";
registerDrawingTool(PriceRange);
registerDrawingTool(DateRange);
registerDrawingTool(BarsCount);
registerDrawingTool(Arrow);
registerDrawingTool(Ray);
registerDrawingTool(ExtendedLine);
registerDrawingTool(Polyline);
registerDrawingTool(DateAndPriceRange);
registerDrawingTool(LongPosition);
registerDrawingTool(ShortPosition);
registerDrawingTool(FibExtension);
registerDrawingTool(ParallelChannel);
registerDrawingTool(Pitchfork);
registerDrawingTool(AbcdPattern);
registerDrawingTool(FibArc);
registerDrawingTool(FibTimeZone);
registerDrawingTool(RegressionChannel);
registerDrawingTool(GannFan);
registerDrawingTool(ElliottImpulse);
registerDrawingTool(ElliottCorrection);
registerDrawingTool(ElliottTriangle);
registerDrawingTool(ElliottDoubleCombo);
registerDrawingTool(ElliottTripleCombo);
registerDrawingTool(XabcdPattern);
registerDrawingTool(CypherPattern);
registerDrawingTool(HeadAndShoulders);
registerDrawingTool(TrianglePattern);
registerDrawingTool(ThreeDrivesPattern);
registerDrawingTool(FibChannel);
registerDrawingTool(FibSpeedResistanceFan);
registerDrawingTool(TrendBasedFibTime);
registerDrawingTool(FibCircles);
registerDrawingTool(FibSpiral);
registerDrawingTool(FibSpeedResistanceArcs);
registerDrawingTool(FibWedge);
registerDrawingTool(Pitchfan);
registerDrawingTool(GannBox);
registerDrawingTool(GannSquare);
registerDrawingTool(GannSquareFixed);
registerDrawingTool(CyclicLines);
registerDrawingTool(TimeCycles);
registerDrawingTool(SineLine);
registerDrawingTool(DisjointChannel);
registerDrawingTool(FlatTopBottom);
registerDrawingTool(SchiffPitchfork);
registerDrawingTool(ModifiedSchiff);
registerDrawingTool(InsidePitchfork);
registerDrawingTool(GannFixed);
registerDrawingTool(TrendAngle);
registerDrawingTool(InfoLine);
// CE24: Drawing Tools Pack 3
registerDrawingTool(AnchoredVWAP);
registerDrawingTool(IconMarker);
registerDrawingTool(GhostFeed);
registerDrawingTool(BarsPattern);
registerDrawingTool(CrossLine);
registerDrawingTool(DatePriceRange);
// CE23: Drawing Tools Pack 2
registerDrawingTool(Brush);
registerDrawingTool(Highlighter);
registerDrawingTool(Eraser);
registerDrawingTool(Curve);
registerDrawingTool(Arc);
registerDrawingTool(Path);
registerDrawingTool(Polygon);
registerDrawingTool(RotatedRectangle);
export { createDraftFromTool, createTool, resolvePointAnchor, createDrawingHistory, deserializeDrawingHistory, deserializeDrawingObject, deserializeDrawings, drawingReducer, historyReducer, isDrawingToolName, listDrawingTools, listDrawingPlugins, registerDrawingTool, registerDrawingPlugin, getDrawingPlugin, serializeDrawingHistory, serializeDrawingObject, serializeDrawings, exportDrawingsJSON, importDrawingsJSON, chartPointToPixel, chartPointToPixelWithBridge, pixelToChartPoint, buildRenderScalesFromBridge, measurementPointToPixelWithBridge, measurementPointToPixel, resolveChartPointWithBridge, resolveMeasurementPointWithBridge, resolveMeasurementPoint, summarizeMeasurement, createDrawingInteractionState, deleteSelectedInteractionState, drawingInteractionReducer, createLocalStorageAdapter, createRESTStorageAdapter, DrawingImportError, DrawingStorageError, };
export { MAGNET_TOLERANCE, collectFibLevelSnapCandidates } from "./snap";
export { HIT_TOLERANCE } from "./hitTest";
export { DOCK_STATE_KEY, DRAWINGS_KEY_PREFIX, LIST_COLLAPSED_KEY, TEMPLATE_DEFAULT_PREFIX, TEMPLATE_INDEX_PREFIX, TEMPLATE_LEGACY_PREFIX, TEMPLATE_NAMED_PREFIX, WIDGET_CLIPBOARD_KEY, drawingsAllKey, drawingsKey, } from "./storageKeys";
export { TrendLine, HLine, VLine, Fibonacci, Channel, Text, Rectangle, Arrow, Ray, ExtendedLine, Polyline, DateAndPriceRange, LongPosition, ShortPosition, FibExtension, ParallelChannel, Pitchfork, AbcdPattern, FibArc, FibTimeZone, RegressionChannel, ElliottImpulse, ElliottCorrection, ElliottTriangle, ElliottDoubleCombo, ElliottTripleCombo, XabcdPattern, CypherPattern, HeadAndShoulders, TrianglePattern, ThreeDrivesPattern, FibChannel, FibSpeedResistanceFan, TrendBasedFibTime, FibCircles, FibSpiral, FibSpeedResistanceArcs, FibWedge, Pitchfan, GannBox, GannSquare, GannSquareFixed, CyclicLines, TimeCycles, SineLine, DisjointChannel, FlatTopBottom, SchiffPitchfork, ModifiedSchiff, InsidePitchfork, GannFixed, TrendAngle, InfoLine, 
// CE24: Drawing Tools Pack 3
AnchoredVWAP, IconMarker, GhostFeed, BarsPattern, CrossLine, DatePriceRange, 
// CE23: Drawing Tools Pack 2
Brush, Highlighter, Eraser, Curve, Arc, Path, Polygon, RotatedRectangle, };
// ── GAP-4: Multi-select batch operations ─────────────────────────────────────
export { applyBatchDelete, applyBatchLock, applyBatchVisible, applyBatchColor, resolveNextLockState, resolveNextVisibleState, } from "./multiSelectBatch";
export { classifyHarmonicDeviation, computeHarmonicRatioLabels, formatHarmonicRatioLabel, } from "./harmonicRatioValidation";
export { appendPoint } from "./shared";
export { createDrawingObject } from "./shared";
export { numberFormatter } from "./shared";
export { cloneDrawingSnapshot } from "./clipboard";
export { offsetDrawingByPixels } from "./clipboard";
export { SEGMENT_KEYS_BY_TYPE } from "./segmentRegistry";
export { getSegmentKeys } from "./segmentRegistry";
export { hasSubSegments } from "./segmentRegistry";
export { isSegmentVisible } from "./segmentRegistry";
export { resolveSegmentStyle } from "./segmentRegistry";
export { isTypingTarget } from "./keyboardGuard";
export { getSelectedObjectIds } from "./drawingInteractionState";
export { patchDrawing, replaceDrawingById } from "./drawingInteractionState";
export { toOverlayPrimitive, registerPrimitiveBuilder, unregisterPrimitiveBuilder, hasPrimitiveBuilder } from "./overlayPrimitiveAdapter";
export { TOOL_CAPABILITY_REGISTRY } from "./toolCapabilityRegistry";
export { calculateAnchoredVwapGeometry } from "./builtin/anchoredVwap";
export { calculateBarsPatternGeometry } from "./builtin/barsPattern";
export { DEFAULT_FIB_ARC_LEVELS } from "./builtin/fibArc";
export { calculateFibArcGeometry } from "./builtin/fibArc";
export { DEFAULT_FIB_TIME_ZONE_LEVELS } from "./builtin/fibTimeZone";
export { calculateFibTimeZoneGeometry } from "./builtin/fibTimeZone";
export { calculateAbcdPatternMetrics } from "./builtin/abcdPattern";
export { calculateParallelChannelGeometry } from "./builtin/parallelChannel";
export { calculatePitchforkGeometry } from "./builtin/pitchfork";
export { calculateRegressionChannelMetrics } from "./builtin/regressionChannel";
// Phase 4 (PLAN_DRAWING_SSOT_MIGRATION_2026-09-27): the widget codemod needs these
// symbols through the root entry as well. Additive only - nothing is renamed or removed.
export { loadTemplateData } from "./drawingTemplateStorage";
export { resolveSemanticPointLabels } from "./renderCanvas";
export { isToolAvailable } from "./drawingTierUtils";
export { loadDrawingTemplateData } from "./drawingStyleRegistry";
export { resolveDrawingPriceMarkers } from "./priceLabel";
export { getDrawingBoundingBox } from "./hitTest";
export { isDrawingInRect } from "./hitTest";
export { enrichDrawingWithIndex } from "./coordinateEnricher";
export { replacePoint } from "./shared";
export { createDrawingContextMenuItems } from "./drawingActionSurface";
export { isValidDrawingSnapshot } from "./clipboard";
export { createDrawingInteractionCommand } from "./drawingInteractionBridge";
export { isForkSupportedDrawingAuthoringTool } from "./toolCapabilityRegistry";
export { buildToolCoverageMatrix } from "./toolCapabilityRegistry";
export { createCanvasContextMenuItems } from "./drawingActionSurface";
export { hitTestDrawingWithContext } from "./drawingInteractionBridge";
export { isForkSupportedDrawingManipulationType } from "./toolCapabilityRegistry";
export { resolveDrawingManipulationIntentWithContext } from "./drawingInteractionBridge";
export { resolveDrawingInteractionContext } from "./drawingInteractionBridge";
export { resolveDrawingPointWithContext } from "./drawingInteractionBridge";
